import { useEffect, useState } from 'react';
import { getDashboardApi, getSalesReportApi } from '../../api/adminApi';
import { toast } from 'react-toastify';

const STATUS_COLOR = {
  pending:    '#F59E0B', confirmed: '#3B82F6', processing: '#8B5CF6',
  shipped:    '#06B6D4', delivered: '#16A34A',  cancelled:  '#EF4444',
};

function BarChart({ data }) {
  if (!data || !data.length) return <div style={{ height: 180, display:'flex', alignItems:'center', justifyContent:'center', color:'#aaa', fontSize:'13px' }}>No revenue data yet</div>;
  const reversed = [...data].reverse();
  const max = Math.max(...reversed.map((d) => parseFloat(d.revenue || d.revenue)), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '160px', padding: '0 4px' }}>
      {reversed.map((d, i) => {
        const h = Math.max((parseFloat(d.revenue) / max) * 140, 4);
        const label = new Date(d.month || d.period).toLocaleDateString('en-IN', { month: 'short' });
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '9px', color: '#888', fontWeight: 600 }}>
              {parseFloat(d.revenue) > 0 ? `₹${(parseFloat(d.revenue)/1000).toFixed(0)}k` : ''}
            </span>
            <div style={{ width: '100%', height: `${h}px`, background: i === reversed.length - 1 ? 'var(--red)' : '#1f2937', borderRadius: '2px 2px 0 0', transition: 'height .4s ease', cursor: 'default' }} title={`₹${parseFloat(d.revenue).toLocaleString('en-IN')}`} />
            <span style={{ fontSize: '9px', color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function Kpi({ label, value, sub, icon, color, textColor }) {
  return (
    <div style={S.kpiCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={S.kpiLabel}>{label}</p>
          <p style={S.kpiVal}>{value}</p>
          {sub && <p style={S.kpiSub}>{sub}</p>}
        </div>
        <div style={{ ...S.kpiIcon, background: color, color: textColor }}>{icon}</div>
      </div>
    </div>
  );
}

export default function DashTab({ user }) {
  const [data, setData]     = useState(null);
  const [report, setReport] = useState([]);
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getDashboardApi(), getSalesReportApi({ period })])
      .then(([d, r]) => { setData(d.data); setReport(r.data); })
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div style={S.loadingBox}><div style={S.spinner} /></div>;
  if (!data)   return null;

  const statusMap = {};
  (data.status_breakdown || []).forEach((s) => { statusMap[s.status] = parseInt(s.count); });

  return (
    <div>
      {/* Greeting */}
      <div style={S.greeting}>
        <div>
          <h1 style={S.pageTitle}>Welcome back, <em style={{ color: 'var(--red)', fontStyle: 'normal' }}>{user?.name?.split(' ')[0]}</em> 👋</h1>
          <p style={S.pageSub}>Here's what's happening with Bhadralok today.</p>
        </div>
        <div style={S.dateChip}>{new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}</div>
      </div>

      {/* KPI Cards */}
      <div style={S.kpiGrid}>
        <Kpi label="Total Revenue" value={`₹${parseFloat(data.total_sales).toLocaleString('en-IN')}`}
          sub="Delivered orders" icon="₹" color="#FEF9C3" textColor="#854D0E" />
        <Kpi label="Total Orders" value={data.total_orders}
          sub={`${data.pending_orders} pending`} icon="📦" color="#DBEAFE" textColor="#1D4ED8" />
        <Kpi label="Customers" value={data.total_customers}
          sub="Registered" icon="👥" color="#EDE9FE" textColor="#6D28D9" />
        <Kpi label="Active Products" value={data.total_products}
          sub={`${(data.low_stock || []).length} low stock`} icon="🏷️" color="#DCFCE7" textColor="#16A34A" />
      </div>

      {/* Charts Row */}
      <div style={S.chartsRow}>
        {/* Revenue Chart */}
        <div style={S.chartCard}>
          <div style={S.cardHead}>
            <span style={S.cardTitle}>REVENUE OVERVIEW</span>
            <div style={S.toggleGroup}>
              {['monthly','daily'].map((p) => (
                <button key={p} style={{ ...S.toggleBtn, background: period === p ? '#111' : '#fff', color: period === p ? '#fff' : '#666' }}
                  onClick={() => setPeriod(p)}>{p === 'monthly' ? 'Monthly' : 'Daily'}</button>
              ))}
            </div>
          </div>
          <BarChart data={report.length ? report : data.revenue_chart} />
        </div>

        {/* Order Status */}
        <div style={S.chartCard}>
          <div style={S.cardHead}>
            <span style={S.cardTitle}>ORDER STATUS</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
            {['pending','confirmed','processing','shipped','delivered','cancelled'].map((s) => {
              const count = statusMap[s] || 0;
              const total = data.total_orders || 1;
              const pct   = Math.round((count / total) * 100);
              return (
                <div key={s}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, textTransform: 'capitalize', color: '#333' }}>{s}</span>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: STATUS_COLOR[s] }}>{count}</span>
                  </div>
                  <div style={{ height: '5px', background: '#F3F4F6', borderRadius: '99px' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: STATUS_COLOR[s], borderRadius: '99px', transition: 'width .4s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom Row: Recent Orders + Low Stock */}
      <div style={S.bottomRow}>
        {/* Recent Orders */}
        <div style={{ ...S.tableCard, flex: 2 }}>
          <div style={S.cardHead}><span style={S.cardTitle}>RECENT ORDERS</span></div>
          <table style={S.table}>
            <thead>
              <tr>{['Order','Customer','Amount','Status','Date'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.recent_orders.map((o) => (
                <tr key={o.id} style={S.tr}>
                  <td style={S.td}><span style={S.orderId}>#{o.id.slice(0,8).toUpperCase()}</span></td>
                  <td style={S.td}><span style={{ fontWeight: 600 }}>{o.customer_name}</span></td>
                  <td style={S.td}><strong>₹{parseFloat(o.total_amount).toLocaleString('en-IN')}</strong></td>
                  <td style={S.td}><span style={{ ...S.badge, background: STATUS_COLOR[o.status] + '22', color: STATUS_COLOR[o.status], border: `1px solid ${STATUS_COLOR[o.status]}40` }}>{o.status}</span></td>
                  <td style={S.td}>{new Date(o.placed_at).toLocaleDateString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Low Stock */}
        <div style={{ ...S.tableCard, flex: 1 }}>
          <div style={S.cardHead}><span style={S.cardTitle}>⚠️ LOW STOCK</span></div>
          {(data.low_stock || []).length === 0
            ? <p style={{ fontSize: '13px', color: '#aaa', padding: '20px 0', textAlign: 'center' }}>All products well stocked 🎉</p>
            : (data.low_stock || []).map((p) => (
              <div key={p.id} style={S.lowStockRow}>
                <span style={{ fontSize: '13px', fontWeight: 600, flex: 1, color: '#111' }}>{p.name}</span>
                <span style={{ ...S.badge, background: p.stock === 0 ? '#FEE2E2' : '#FEF3C7', color: p.stock === 0 ? '#DC2626' : '#D97706', border: 'none', flexShrink: 0 }}>
                  {p.stock === 0 ? 'Out of Stock' : `${p.stock} left`}
                </span>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}

const S = {
  loadingBox: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' },
  spinner:    { width: '36px', height: '36px', border: '3px solid #E5E5E5', borderTopColor: 'var(--red)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  greeting:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  pageTitle:  { fontFamily: 'var(--font-display)', fontSize: '28px', letterSpacing: '1px', color: '#111', lineHeight: 1.1, margin: 0 },
  pageSub:    { fontSize: '13px', color: '#888', marginTop: '4px' },
  dateChip:   { fontSize: '12px', color: '#666', fontWeight: 600, background: '#fff', border: '1px solid #E5E5E5', padding: '6px 14px' },
  kpiGrid:    { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '16px', marginBottom: '20px' },
  kpiCard:    { background: '#fff', border: '1px solid #E5E5E5', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,.04)' },
  kpiLabel:   { fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#9CA3AF', marginBottom: '6px' },
  kpiVal:     { fontFamily: 'var(--font-display)', fontSize: '28px', letterSpacing: '1px', lineHeight: 1, color: '#111', marginBottom: '4px' },
  kpiSub:     { fontSize: '11px', color: '#9CA3AF', fontWeight: 600 },
  kpiIcon:    { width: '40px', height: '40px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 },
  chartsRow:  { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '20px' },
  chartCard:  { background: '#fff', border: '1px solid #E5E5E5', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,.04)' },
  cardHead:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  cardTitle:  { fontFamily: 'var(--font-display)', fontSize: '12px', letterSpacing: '2.5px', color: '#9CA3AF' },
  toggleGroup:{ display: 'flex', border: '1px solid #E5E5E5', overflow: 'hidden' },
  toggleBtn:  { padding: '5px 12px', border: 'none', fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 600, cursor: 'pointer', transition: 'all .15s' },
  bottomRow:  { display: 'flex', gap: '16px' },
  tableCard:  { background: '#fff', border: '1px solid #E5E5E5', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,.04)' },
  table:      { width: '100%', borderCollapse: 'collapse' },
  th:         { padding: '9px 12px', fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#9CA3AF', borderBottom: '1px solid #F3F4F6', textAlign: 'left', background: '#FAFAFA' },
  tr:         { borderBottom: '1px solid #F9FAFB', transition: 'background .1s' },
  td:         { padding: '11px 12px', fontSize: '13px', verticalAlign: 'middle' },
  badge:      { display: 'inline-block', fontSize: '10px', fontWeight: 700, letterSpacing: '.5px', textTransform: 'capitalize', padding: '3px 9px', borderRadius: '99px' },
  orderId:    { fontFamily: 'var(--font-display)', fontSize: '12px', letterSpacing: '1px', color: '#374151' },
  lowStockRow:{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F3F4F6', gap: '12px' },
};
